import { Text, useColorModeValue } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import React, { forwardRef, useCallback, useMemo, useState } from "react";

import CompanyDisplay from "@/common/components/CompanyDisplay";
import { ListType } from "@/common/components/MultiSelector/MultiSelector";
import { MultiSelectorPopover } from "@/common/components/MultiSelector/MultiSelectorPopover";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { companyQueryKeys } from "@/company/data/companyQueryKeys";
import { useCompaniesSearch } from "@/company/data/useCompaniesSearch";
import { useCompanyNamesData } from "@/company/data/useCompanyNamesData";
import { GroupSummary } from "@/feature/components/AccessSummary";
import { COMPANY_ID_FIELD_KEY } from "@reflag/shared/attributeFilter";
import { CompanyName } from "@reflag/shared/companyAPI";

import { RuleSimpleSelection } from "./RuleSimpleSelectionProps";
import { SelectorProps } from "./RuleSimpleSelectorTypes";

type RuleSimpleCompanySelectorProps = SelectorProps & {
  envId?: string;
  summaryCount?: number;
  summaryLogoCount?: number;
  alwaysShowSummary?: boolean;
  allowReadOnlyOpen?: boolean;
};

export const RuleSimpleCompanySelector = forwardRef<
  HTMLButtonElement,
  RuleSimpleCompanySelectorProps
>(
  (
    {
      value,
      isReadOnly,
      envId: envIdOverride,
      summaryCount = 9,
      summaryLogoCount = 3,
      alwaysShowSummary = false,
      allowReadOnlyOpen = false,
      onChange: onChangeProp,
    },
    _ref,
  ) => {
    const { appId, envId: currentEnvId } = useEnvironment();
    const envId = envIdOverride ?? currentEnvId;
    const queryClient = useQueryClient();
    const [searchValue, setSearchValue] = useState("");
    const [listType, setListType] = useState<ListType>(
      isReadOnly ? "selected" : "all",
    );

    const {
      data: companyNames = [],
      isFetching: isFetchingCompanyNames,
      isPlaceholderData: isPlaceholderDataCompanyNames,
    } = useCompanyNamesData(
      isReadOnly ? value?.slice(0, summaryCount) : value,
      true,
      envId,
    );

    const isNamesLoading =
      isFetchingCompanyNames && isPlaceholderDataCompanyNames;

    const avatarBackgroundColor = useColorModeValue("gray.50", "gray.750");

    const summary = useMemo(() => {
      if (value && (alwaysShowSummary || value.length > summaryCount)) {
        return {
          slotType: "summary",
          type: "company",
          samples: companyNames.slice(0, summaryLogoCount),
          count: value.length,
        };
      }
      return null;
    }, [
      alwaysShowSummary,
      companyNames,
      summaryLogoCount,
      summaryCount,
      value,
    ]);

    // todo: remove unknown companies when useCompaniesSearch supports it
    const unknownCompanies = useMemo(() => {
      return companyNames.filter(
        (c) => c.isUnknown && (!searchValue || c.id.includes(searchValue)),
      );
    }, [companyNames, searchValue]);

    const {
      data: companies = [],
      isFetching,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage,
    } = useCompaniesSearch(
      searchValue,
      {
        filter:
          listType === "selected"
            ? {
                type: "companyAttribute",
                operator: "ANY_OF",
                field: COMPANY_ID_FIELD_KEY,
                values: value,
              }
            : undefined,
      },
      undefined,
      envId,
    );

    const suggestions = useMemo(() => {
      return [...unknownCompanies, ...companies];
    }, [companies, unknownCompanies]);

    const onChange = useCallback(
      (companyIds: string[], meta?: CompanyName[]) => {
        if (!onChangeProp) return;

        if (meta?.length) {
          // Get the current list of items by ids
          const current = queryClient.getQueryData<CompanyName[]>(
            companyQueryKeys.companyNames(appId, envId, value),
          );

          // Update the list of items by ids
          queryClient.setQueryData<CompanyName[]>(
            companyQueryKeys.companyNames(appId, envId, companyIds),
            () => {
              return companyIds.map((id) => {
                const metaItem = meta?.find((c: CompanyName) => c.id === id);
                const currentItem = current?.find(
                  (c: CompanyName) => c.id === id,
                );

                return (
                  metaItem ??
                  currentItem ?? {
                    id,
                    name: null,
                    avatarUrl: null,
                    isUnknown: true,
                  }
                );
              });
            },
          );
        } else {
          queryClient.invalidateQueries({
            queryKey: companyQueryKeys.companyNames(appId, envId, companyIds),
          });
        }

        if (companyIds.length === 0) setListType("all");
        onChangeProp(companyIds);
      },
      [appId, envId, onChangeProp, queryClient, value],
    );

    return (
      <MultiSelectorPopover
        allowManualEntry={!isReadOnly}
        allowReadOnlyOpen={allowReadOnlyOpen}
        allSelectedToggle={!isReadOnly}
        addRawLabel={(searchValue) => (
          <>
            Add company ID{" "}
            <Text as="span" fontWeight="bold">
              {searchValue}
            </Text>
          </>
        )}
        hasMore={hasNextPage}
        isLoadingMore={isFetchingNextPage}
        isLoadingSuggestions={isFetching}
        isReadOnly={isReadOnly}
        itemRenderFn={(item, search) => (
          <CompanyDisplay company={item} search={search} showId />
        )}
        listType={listType}
        noItemsFound="No companies found"
        searchPlaceholder="Search companies or enter company ID"
        searchValue={searchValue}
        suggestions={suggestions}
        value={value}
        onChange={onChange}
        onListTypeChange={setListType}
        onLoadMore={fetchNextPage}
        onSearchInput={setSearchValue}
      >
        {summary ? (
          <RuleSimpleSelection
            isLoading={isNamesLoading}
            readOnly={isReadOnly}
            isSummary
          >
            <GroupSummary
              avatarBackgroundColor={avatarBackgroundColor}
              count={summary.count}
              entityType="company"
              sampleEntities={summary.samples}
            />
          </RuleSimpleSelection>
        ) : (
          companyNames.map((company) => (
            <RuleSimpleSelection
              key={company.id}
              isLoading={isNamesLoading}
              readOnly={isReadOnly}
              onRemove={() => {
                if (!value) return;
                const newValue = value.filter((id) => id !== company.id);
                onChange?.(newValue);
              }}
            >
              <CompanyDisplay company={company} />
            </RuleSimpleSelection>
          ))
        )}
      </MultiSelectorPopover>
    );
  },
);
RuleSimpleCompanySelector.displayName = "RuleSimpleCompanySelector";
